import { useContext, useLayoutEffect, type ReactNode } from "react";
import { Text, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import { semanticColors } from "../theme/tokens";
import { DIALOG_TITLE_LINE_HEIGHT, DialogChromeContext } from "./Dialog";
import { SectionTitle } from "./SectionTitle";

type TextProps = {
  children: ReactNode;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
};

/** Display title role reserved for dialog and sheet headers. */
export function DialogTitleText({ children, numberOfLines, style }: TextProps) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={numberOfLines}
      style={[
        {
          fontSize: 20,
          lineHeight: DIALOG_TITLE_LINE_HEIGHT,
          color: semanticColors.text.primary,
          fontWeight: "900",
          flexShrink: 1,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

/** Body/supporting role reserved for dialog and sheet explanatory copy. */
export function DialogBodyText({ children, numberOfLines, style }: TextProps) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={numberOfLines}
      style={[
        {
          fontSize: 13,
          lineHeight: 18,
          color: semanticColors.text.secondary,
          marginTop: 6,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

type HeaderProps = {
  /** A short uppercase line above the title, naming what kind of thing it is. */
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  /**
   * `start` (the default) left-aligns the title with any small icon beside
   * it, level with the dialog's close. `center` is for a dialog that opens
   * with a big illustration: the illustration, title and body are centred.
   */
  align?: "start" | "center";
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subtitleStyle?: StyleProp<TextStyle>;
};

const CENTERED_TEXT: TextStyle = { textAlign: "center", width: "100%" };
// Room kept on both sides of a centred title when a control sits at its
// right, so the title stays centred and never runs under the control.
const TRAILING_ROOM = 36;
// Keeps a left-aligned title clear of the close at the right.
const CLOSE_ROOM = 32;

/**
 * Shared title/body anatomy for dialogs and bottom sheets (#3848): centred
 * only under a big illustration; otherwise left-aligned, with a small icon
 * beside the title and the title level with the close.
 */
export function DialogHeader({
  eyebrow,
  title,
  subtitle,
  leading,
  trailing,
  align = "start",
  style,
  titleStyle,
  subtitleStyle,
}: HeaderProps) {
  const { closeButtonShown, setTitleBesideClose } =
    useContext(DialogChromeContext);
  // Beside the dialog's own close, a left title asks the dialog to start its
  // content on the close's row.
  const besideClose = align === "start" && closeButtonShown && !trailing;
  useLayoutEffect(() => {
    if (!besideClose) return;
    setTitleBesideClose(true);
    return () => setTitleBesideClose(false);
  }, [besideClose, setTitleBesideClose]);

  if (align === "center") {
    return (
      <View style={[{ gap: 6, width: "100%", alignItems: "center" }, style]}>
        {leading ? <View style={{ marginBottom: 4 }}>{leading}</View> : null}
        {eyebrow ? <SectionTitle>{eyebrow}</SectionTitle> : null}
        <View
          style={{
            width: "100%",
            paddingHorizontal: trailing ? TRAILING_ROOM : 0,
          }}
        >
          <DialogTitleText style={[CENTERED_TEXT, titleStyle]}>
            {title}
          </DialogTitleText>
          {trailing ? (
            <View style={{ position: "absolute", top: 0, right: 0 }}>
              {trailing}
            </View>
          ) : null}
        </View>
        {subtitle ? (
          <DialogBodyText
            style={[{ marginTop: 0 }, CENTERED_TEXT, subtitleStyle]}
          >
            {subtitle}
          </DialogBodyText>
        ) : null}
      </View>
    );
  }

  return (
    <View style={[{ gap: 6 }, style]}>
      {eyebrow ? (
        <View style={{ paddingRight: besideClose ? CLOSE_ROOM : 0 }}>
          <SectionTitle>{eyebrow}</SectionTitle>
        </View>
      ) : null}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          paddingRight: besideClose ? CLOSE_ROOM : 0,
        }}
      >
        {leading}
        {/* With a control after it, the title fills the row so the control
            sits at the right edge. */}
        <DialogTitleText
          style={[trailing ? { flex: 1 } : { flexShrink: 1 }, titleStyle]}
        >
          {title}
        </DialogTitleText>
        {trailing}
      </View>
      {subtitle ? (
        <DialogBodyText style={[{ marginTop: 0 }, subtitleStyle]}>
          {subtitle}
        </DialogBodyText>
      ) : null}
    </View>
  );
}

export default DialogHeader;
